import { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, Truck, Package, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';

export default function VansPage() {
  const [vans, setVans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchVans(); }, []);

  const fetchVans = async () => {
    try {
      const response = await api.get('/api/vans');
      const data = response.data.data || [];
      setVans(Array.isArray(data) ? data : (data.items || []));
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const filteredVans = vans.filter(v => 
    (v.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.plateNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              سيارات التوزيع
            </h1>
            <p className="text-gray-600 mt-2">ادارة سيارات التوزيع والشاحنات</p>
          </div>
          <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" />سيارة جديدة
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center"><Truck className="w-6 h-6 text-cyan-600" /></div><div><p className="text-sm text-gray-600">اجمالي السيارات</p><p className="text-2xl font-bold">{vans.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-600">نشطة</p><p className="text-2xl font-bold text-green-700">{vans.filter(v => v.isActive !== false).length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center"><XCircle className="w-6 h-6 text-red-600" /></div><div><p className="text-sm text-gray-600">معطلة</p><p className="text-2xl font-bold text-red-700">{vans.filter(v => v.isActive === false).length}</p></div></CardContent></Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="بحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border rounded-lg" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-cyan-50 border-b border-cyan-200">
            <CardTitle className="flex items-center gap-2 text-cyan-900">
              <Truck className="w-5 h-5" />قائمة السيارات ({filteredVans.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (<div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div><p>جاري التحميل...</p></div>) :
            filteredVans.length === 0 ? (<div className="text-center py-12"><Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">لا توجد سيارات</p></div>) :
            (<div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-right p-3 font-semibold text-sm">#</th>
                <th className="text-right p-3 font-semibold text-sm">اسم السيارة</th>
                <th className="text-right p-3 font-semibold text-sm">رقم اللوحة</th>
                <th className="text-right p-3 font-semibold text-sm">السعة</th>
                <th className="text-right p-3 font-semibold text-sm">الحالة</th>
                <th className="text-right p-3 font-semibold text-sm">اجراءات</th>
              </tr></thead>
              <tbody>
                {filteredVans.map((van, idx) => (
                  <tr key={van.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-600">{idx + 1}</td>
                    <td className="p-3 font-semibold">{van.name || '-'}</td>
                    <td className="p-3 font-mono text-sm">{van.plateNumber || '-'}</td>
                    <td className="p-3"><Badge variant="outline">{van.capacity || 0} شحنة</Badge></td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${van.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{van.isActive !== false ? 'نشطة' : 'معطلة'}</span></td>
                    <td className="p-3"><div className="flex gap-1">
                      <Button variant="outline" size="sm"><Edit className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm"><Trash2 className="w-4 h-4" /></Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table></div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
